import { useMutation, useQueryClient, UseMutationOptions } from "@tanstack/react-query";
import { useCallback } from "react";
import { cacheUtils } from "@/lib/cache";
import { toast } from "sonner";

interface OptimisticMutationOptions<TData, TVariables> extends UseMutationOptions<TData, Error, TVariables> {
  // Cache keys to invalidate on success
  invalidateKeys?: readonly unknown[][];
  
  // Optimistic update configuration
  optimisticUpdate?: {
    queryKey: readonly unknown[];
    updater: (old: any, variables: TVariables) => any;
    rollbackMessage?: string;
  };
  
  // Success/error notifications
  successMessage?: string | ((data: TData, variables: TVariables) => string);
  errorMessage?: string | ((error: Error, variables: TVariables) => string);
  
  // Auto-refresh related queries
  autoRefresh?: readonly unknown[][];
  
  // Background sync after mutation
  backgroundSync?: boolean;
}

export function useOptimisticMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: OptimisticMutationOptions<TData, TVariables> = {}
) {
  const queryClient = useQueryClient();
  
  const {
    optimisticUpdate,
    invalidateKeys = [],
    successMessage,
    errorMessage,
    autoRefresh = [],
    backgroundSync = true,
    ...mutationOptions
  } = options;

  const mutation = useMutation({
    mutationFn,
    onMutate: async (variables) => {
      let rollback: (() => void) | undefined;
      
      // Perform optimistic update if configured
      if (optimisticUpdate) {
        rollback = cacheUtils.optimisticUpdate(
          queryClient,
          optimisticUpdate.queryKey,
          (old) => optimisticUpdate.updater(old, variables)
        );
        
        console.log('🔄 Optimistic update applied for:', optimisticUpdate.queryKey);
      }
      
      // Call original onMutate if provided
      const result = await options.onMutate?.(variables);
      
      return { rollback, ...result };
    },
    
    onSuccess: (data, variables, context) => {
      console.log('✅ Mutation successful:', data);
      
      // Show success message
      if (successMessage) {
        const message = typeof successMessage === 'function' 
          ? successMessage(data, variables) 
          : successMessage;
        toast.success(message);
      }
      
      // Invalidate specified cache keys
      if (invalidateKeys.length > 0) {
        cacheUtils.invalidateRelated(queryClient, invalidateKeys);
        console.log('🗑️ Cache invalidated for keys:', invalidateKeys);
      }
      
      // Auto-refresh specified queries
      autoRefresh.forEach(queryKey => {
        queryClient.refetchQueries({ queryKey });
      });
      
      // Background sync if enabled
      if (backgroundSync) {
        setTimeout(() => {
          queryClient.refetchQueries({ stale: true });
        }, 1000);
      }
      
      // Call original onSuccess if provided
      options.onSuccess?.(data, variables, context);
    },
    
    onError: (error, variables, context) => {
      console.error('❌ Mutation failed:', error);
      
      // Rollback optimistic update
      if (context?.rollback) {
        context.rollback();
        console.log('⏪ Optimistic update rolled back');
        
        if (optimisticUpdate?.rollbackMessage) {
          toast.warning(optimisticUpdate.rollbackMessage);
        }
      }
      
      // Show error message
      if (errorMessage) {
        const message = typeof errorMessage === 'function' 
          ? errorMessage(error, variables) 
          : errorMessage;
        toast.error(message);
      } else {
        // Default error message
        toast.error('Something went wrong. Please try again.');
      }
      
      // Call original onError if provided
      options.onError?.(error, variables, context);
    },
    
    onSettled: (data, error, variables, context) => {
      // Call original onSettled if provided
      options.onSettled?.(data, error, variables, context);
    },
    
    ...mutationOptions,
  });

  // Enhanced mutation with additional utilities
  return {
    ...mutation,
    
    // Utility to manually trigger cache invalidation
    invalidateCache: useCallback(() => {
      if (invalidateKeys.length > 0) {
        cacheUtils.invalidateRelated(queryClient, invalidateKeys);
      }
    }, [queryClient]),
    
    // Utility to refresh related queries
    refreshQueries: useCallback(() => {
      autoRefresh.forEach(queryKey => {
        queryClient.refetchQueries({ queryKey });
      });
    }, [queryClient]),
    
    // Utility to check if mutation is in progress
    isUpdating: mutation.isPending,
    
    // Utility to get mutation timestamp
    lastUpdated: mutation.submittedAt,
  };
}

// Specialized hooks for common mutation patterns

// Product mutations (add to cart, update product, etc.)
export function useProductMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: Omit<OptimisticMutationOptions<TData, TVariables>, 'backgroundSync'> = {}
) {
  return useOptimisticMutation(mutationFn, {
    ...options,
    backgroundSync: true, // Always sync product data
  });
}

// User mutations (update profile, preferences, etc.)
export function useUserMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: OptimisticMutationOptions<TData, TVariables> = {}
) {
  return useOptimisticMutation(mutationFn, {
    ...options,
    // Auto-invalidate user-related cache
    invalidateKeys: [
      ['user'],
      ['profile'],
      ...(options.invalidateKeys || [])
    ],
  });
}

// Cart mutations (add, remove, update quantity)
export function useCartMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: Omit<OptimisticMutationOptions<TData, TVariables>, 'invalidateKeys'> = {}
) {
  return useOptimisticMutation(mutationFn, {
    ...options,
    invalidateKeys: [
      ['cart'],
      ['cart', 'count'],
    ],
    backgroundSync: false, // Cart updates don't need background sync
  });
}

// Order mutations (create order, update status, etc.)
export function useOrderMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: OptimisticMutationOptions<TData, TVariables> = {}
) {
  return useOptimisticMutation(mutationFn, {
    ...options,
    invalidateKeys: [
      ['orders'],
      ['cart'], // Clear cart after order
      ...(options.invalidateKeys || [])
    ],
  });
}